import React, { useCallback, useEffect, useRef, useState, useMemo } from 'react';
import service from '../../services/resourceCenterService';
import { captureSession, isSessionCurrent, SESSION_EVENT } from '../../utils/authSession';
import { useT } from '../../i18n/LanguageContext';
import {
  canonicalSize,
  sizeLabel,
  sortedSizeTokens,
} from '../../features/sizeFinder/sizingContract';
import '../../pages/ResourceCenter.css';

export { canonicalSize, sizeLabel } from '../../features/sizeFinder/sizingContract';
export function useSizingPreferences(enabled, distributorId, readOnly = false) {
  const scopeArgs = useMemo(() => (distributorId ? [distributorId] : []), [distributorId]);
  const [catalog, setCatalog] = useState(null);
  const [preferences, setPreferences] = useState(null);
  const [excluded, setExcluded] = useState([]);
  const [loadError, setLoadError] = useState(false);
  const [sessionEnded, setSessionEnded] = useState(false);
  const [saving, setSaving] = useState(false);
  const [notice, setNotice] = useState('');
  const [saveError, setSaveError] = useState('');
  const alive = useRef(true);
  const session = useRef(captureSession());
  const sequence = useRef(0);
  const lock = useRef(false);
  const current = useCallback(() => alive.current && isSessionCurrent(session.current), []);
  const invalidatePending = useCallback(() => {
    sequence.current++;
  }, []);
  useEffect(() => {
    alive.current = true;
    const changed = () => {
      if (!isSessionCurrent(session.current)) {
        sequence.current++;
        setSessionEnded(true);
        setCatalog(null);
        setPreferences(null);
        setExcluded([]);
        setSaving(false);
        setNotice('');
        setSaveError('');
      }
    };
    window.addEventListener(SESSION_EVENT, changed);
    window.addEventListener('storage', changed);
    return () => {
      alive.current = false;
      invalidatePending();
      window.removeEventListener(SESSION_EVENT, changed);
      window.removeEventListener('storage', changed);
    };
  }, [invalidatePending]);
  const load = useCallback(async () => {
    const request = ++sequence.current;
    setCatalog(null);
    setPreferences(null);
    setLoadError(false);
    setNotice('');
    setSaveError('');
    try {
      const [nextCatalog, nextPreferences] = await Promise.all([
        service.catalog(...scopeArgs),
        service.getSizingPreferences(...scopeArgs),
      ]);
      if (
        !Array.isArray(nextPreferences.excludedSizeTokens) ||
        !Number.isInteger(nextPreferences.revision)
      )
        throw new Error('Invalid preferences');
      if (current() && request === sequence.current) {
        setCatalog(nextCatalog);
        setPreferences(nextPreferences);
        setExcluded(nextPreferences.excludedSizeTokens);
      }
    } catch {
      if (current() && request === sequence.current) setLoadError(true);
    }
  }, [current, scopeArgs]);
  useEffect(() => {
    if (enabled) load();
    else {
      sequence.current++;
      setCatalog(null);
      setPreferences(null);
    }
  }, [enabled, load]);
  const change = (values) => {
    setExcluded(values);
    setNotice('');
  };
  const save = async () => {
    if (readOnly || lock.current || !preferences || saveError || !current()) return;
    lock.current = true;
    setSaving(true);
    setNotice('');
    const request = sequence.current;
    try {
      const next = await service.saveSizingPreferences(
        {
          excludedSizeTokens: excluded,
          revision: preferences.revision,
        },
        ...scopeArgs
      );
      if (current() && request === sequence.current) {
        setPreferences(next);
        setExcluded(next.excludedSizeTokens);
        setNotice('Your size defaults are saved.');
      }
    } catch (error) {
      if (current() && request === sequence.current)
        setSaveError(
          error?.response?.status === 409
            ? 'Your defaults changed elsewhere. Reload saved defaults before saving again.'
            : 'Saving could not be confirmed. Reload saved defaults before saving again.'
        );
    } finally {
      lock.current = false;
      if (current()) setSaving(false);
    }
  };
  return {
    readOnly,
    sessionEnded,
    catalog,
    preferences,
    excluded,
    change,
    loadError,
    load,
    saving,
    save,
    notice,
    saveError,
  };
}
export function SizeExclusions({ sizes, state, onChange = state.change }) {
  const t = useT();
  const tokens = sortedSizeTokens(sizes, state.excluded);
  return (
    <div className="rc-size-preferences">
      <fieldset disabled={state.saving}>
        <legend>{t('Exclude sizes')}</legend>
        <p className="rc-muted">
          {t('Checked sizes will not be suggested. Applies to every product.')}
        </p>
        <div className="rc-size-options">
          {tokens.map((token) => (
            <label key={token} className="rc-check">
              <input
                type="checkbox"
                checked={state.excluded.includes(token)}
                onChange={(e) =>
                  onChange(
                    e.target.checked
                      ? [...state.excluded, token]
                      : state.excluded.filter((value) => value !== token)
                  )
                }
              />
              {sizeLabel(token)}
            </label>
          ))}
        </div>
        {!tokens.length && <p className="rc-muted">{t('No verified sizes are available.')}</p>}
      </fieldset>
      {!state.readOnly && (
        <button
          type="button"
          onClick={state.save}
          disabled={state.saving || !!state.saveError || !state.preferences}
        >
          {t(state.saving ? 'Saving…' : 'Save as my defaults')}
        </button>
      )}
      {state.notice && (
        <p role="status" className="rc-muted">
          {t(state.notice)}
        </p>
      )}
      {state.saveError && (
        <p role="alert" className="rc-error">
          {t(state.saveError)}
        </p>
      )}
      {state.saveError && (
        <button type="button" onClick={state.load} disabled={state.saving}>
          {t('Reload saved defaults')}
        </button>
      )}
    </div>
  );
}
function ProfilePreferences() {
  const state = useSizingPreferences(true);
  const t = useT();
  if (state.sessionEnded)
    return (
      <p role="status">{t('Your session changed. Sign in again to load your size defaults.')}</p>
    );
  return (
    <section className="rc-page rc-profile-sizes" aria-label={t('Size Finder defaults')}>
      <h2>{t('Size Finder defaults')}</h2>
      <p className="rc-muted">
        {t('Saved for your account across browsers. You can change these anytime.')}
      </p>
      {state.loadError ? (
        <div role="alert">
          <p>{t('Could not load products and your saved size defaults. Please retry.')}</p>
          <button onClick={state.load}>{t('Retry')}</button>
        </div>
      ) : !state.catalog ? (
        <p role="status">{t('Loading size defaults…')}</p>
      ) : (
        <SizeExclusions
          state={state}
          sizes={state.catalog.groups.flatMap((group) => group.products.map(canonicalSize))}
        />
      )}
    </section>
  );
}
export default function SizePreferences() {
  const [enabled, setEnabled] = useState(false);
  useEffect(() => {
    let alive = true;
    const session = captureSession();
    service
      .config()
      .then((config) => {
        if (alive && isSessionCurrent(session))
          setEnabled(!!config.enabled && !!config.sizingEnabled);
      })
      .catch(() => {});
    return () => {
      alive = false;
    };
  }, []);
  return enabled ? <ProfilePreferences /> : null;
}
